import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  OYH_REASON_ANSWER_FIELD,
  OYH_REASON_OTHER_ANSWER_FIELD,
  OYH_REASON_SUBJECT_TYPE,
  OYH_REASON_SURVEY_SLUG,
  type OnYourHonorReasonId,
} from '../model/onYourHonor';

import {
  getMySurveyResponsesForSubjectQueryOptions,
  getPublishedSurveyBySlugQueryOptions,
  submitSubjectSurveyResponse,
} from '@/features/surveys';
import { useUserScopedKey, useUserScopedQuery } from '@/hooks/useUserScopedQuery';
import {
  trackOnYourHonorReasonDismissed,
  trackOnYourHonorReasonSubmitted,
  trackOnYourHonorReasonViewed,
} from '@/util/analyticsEvents';
import { Sentry } from '@/util/sentry';

type UseOnYourHonorReasonSurveyArgs = {
  conversationId: string;
  /** Inquiry the reason ties to; its own id is the response subject id. */
  inquiryId?: string | number;
  recipientId?: string | number;
  /** Mirrors `canMarkOnYourHonor`; gates the definition and answered-check queries. */
  enabled: boolean;
};

/**
 * Backs the "above and beyond" reason survey: fetches the published `oyh-reason`
 * definition, submits the chosen reason linked to the inquiry, and decides
 * whether the sheet should open at all.
 *
 * Don't-reshow rests on `alreadyAnswered` alone, a persisted response for this
 * inquiry covering other devices and past sessions. See {@link
 * shouldPromptReason} for why there is no session-level guard beside it.
 *
 * Submission is fire-and-forget from the UI's perspective — the sheet thanks and
 * closes on its own; a failed POST is reported to Sentry, not surfaced, so a
 * transient survey error never costs the expert the mark.
 */
export function useOnYourHonorReasonSurvey({
  conversationId,
  inquiryId,
  recipientId,
  enabled,
}: UseOnYourHonorReasonSurveyArgs) {
  const queryClient = useQueryClient();

  const answeredKey = useUserScopedKey(getMySurveyResponsesForSubjectQueryOptions);

  const subjectId = inquiryId != null ? String(inquiryId) : undefined;

  /** Keeps the immutable definition warm so the submit path has its id ready. */
  useQuery({
    ...getPublishedSurveyBySlugQueryOptions(OYH_REASON_SURVEY_SLUG),
    enabled,
  });

  const { data: existingResponses } = useUserScopedQuery(
    getMySurveyResponsesForSubjectQueryOptions,
    [{ type: OYH_REASON_SUBJECT_TYPE, id: subjectId ?? '' }],
    { enabled: enabled && subjectId != null }
  );

  const alreadyAnswered = (existingResponses?.length ?? 0) > 0;

  /**
   * Whether to ask for a reason on this press: only when the survey is enabled
   * (feature flag + markable) and the inquiry has no persisted answer.
   *
   * There is deliberately no "already shown this session" guard. Answering is now
   * what applies the mark, so a dismissal costs it and the next press has to ask
   * again; suppressing the second prompt would let it mark with no reason, which
   * is the behavior this flow exists to prevent. The guard made sense only while
   * pressing marked the inquiry outright and the sheet was an afterthought.
   */
  const shouldPromptReason = useCallback(
    () => enabled && !alreadyAnswered,
    [enabled, alreadyAnswered]
  );

  const trackReasonViewed = useCallback((): void => {
    trackOnYourHonorReasonViewed({ conversationId, inquiryId, recipientId });
  }, [conversationId, inquiryId, recipientId]);

  const trackReasonDismissed = useCallback((): void => {
    trackOnYourHonorReasonDismissed({ conversationId, inquiryId, recipientId });
  }, [conversationId, inquiryId, recipientId]);

  const submit = useCallback(
    async (answers: Record<string, unknown>, reason: string, isCustom: boolean) => {
      try {
        const definition = await queryClient.ensureQueryData(
          getPublishedSurveyBySlugQueryOptions(OYH_REASON_SURVEY_SLUG)
        );

        if (!definition) {
          return;
        }

        await submitSubjectSurveyResponse({
          surveyDefinitionId: definition.id,
          answers,
          subject: subjectId != null ? { type: OYH_REASON_SUBJECT_TYPE, id: subjectId } : undefined,
        });

        if (subjectId != null) {
          queryClient
            .invalidateQueries({
              queryKey: answeredKey({ type: OYH_REASON_SUBJECT_TYPE, id: subjectId }),
            })
            .catch(() => undefined);
        }

        trackOnYourHonorReasonSubmitted({
          conversationId,
          inquiryId,
          recipientId,
          reason,
          isCustom,
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'useOnYourHonorReasonSurvey', source: 'submit-reason' },
          extra: { conversationId, inquiryId },
        });
      }
    },
    [queryClient, answeredKey, subjectId, conversationId, inquiryId, recipientId]
  );

  const submitReasonChip = useCallback(
    (id: OnYourHonorReasonId) => submit({ [OYH_REASON_ANSWER_FIELD]: id }, id, false),
    [submit]
  );

  const submitReasonCustom = useCallback(
    (text: string) => submit({ [OYH_REASON_OTHER_ANSWER_FIELD]: text }, text, true),
    [submit]
  );

  return {
    shouldPromptReason,
    trackReasonViewed,
    trackReasonDismissed,
    submitReasonChip,
    submitReasonCustom,
  };
}
